/* eslint-disable @typescript-eslint/camelcase */
import React, { FC, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import Drawer from '@fenderdigital/ui/Drawer';
import Menu, { MenuList, MenuAccordion, MenuAccordionItem, MenuCTA } from '@fenderdigital/ui/Menu';
import Spacer from '@fenderdigital/ui/Spacer';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import useFenderConnect from '@fenderdigital/custom-hooks/hooks/useFenderConnect';
import usePlayProfile from '@fenderdigital/custom-hooks/hooks/useProfile';
import {
  Home,
  HomeFilled,
  Path,
  PathFilled,
  Songs,
  SongsFilled,
  PlayLists,
  PlayListsFill,
  Skills,
  SkillsFilled,
  ChordDiagram,
  Heart,
  HeartFill,
  Articles,
  Megaphone,
  MegaphoneFill,
  Toolkit,
  Support,
} from '@fenderdigital/ui/Icons';
import { Playlist } from '@fenderdigital/api-definitions/play';
import Link from '../Link';
import useGetClientIdCookie from '../../hooks/useGetClientIdCookie';
import segment from '../../core/segment';
import {
  MENU_WIDTH,
  PROFILE_URL,
  getCurrentLevel,
  getNavigationLabel,
} from '../../utils/menu-utils';
import { isLocked } from '../../utils/access-utils';
import { getInstrumentFromPath } from '../../utils/path-utils';
import { getSignOutLink } from '../../lib/cognito';
import Library from '../../lib/library';
import { getFlag } from '../../lib/feature-flags';
import { trackEvent, EventNames } from '../../lib/analytics';
import { PathLevel, DEFAULT_PATH_LEVELS, getIcon, MenuPaths, MenuIDs } from './NavigationConfig';
import { usePathContext } from '../../contexts/PathContext';

interface NavigationProps {
  handleCloseMenu: () => void;
  isMenuVisible?: boolean;
  playlist?: Playlist[];
}

const Navigation: FC<NavigationProps> = ({ handleCloseMenu, isMenuVisible, playlist }) => {
  const [pathLevels, setPathLevels] = useState<PathLevel[]>(DEFAULT_PATH_LEVELS);
  const location = useLocation();
  const [hasLAUSDCookie, hasLoadedCookie] = useGetClientIdCookie();
  const { accessLevel: userAccessLevel } = usePlayUser();
  const { userID } = useFenderConnect();
  const { profile } = usePlayProfile();
  const { colors } = useTheme();
  const isFreemium = getFlag('isFreemium');
  const isTabletUp = useBreakpoint(up('tablet-portrait'));
  const menuWidth = isTabletUp ? MENU_WIDTH : '100%';
  const { data: pathData } = usePathContext();
  const instrument = getInstrumentFromPath(pathData);
  const pathID = profile?.['play-path-id'];

  useEffect(() => {
    const generatePathLevels = (pathPlaylist: Playlist[]): PathLevel[] => {
      return pathPlaylist.map(level => {
        const levelIsLocked = isFreemium && isLocked(level['access-levels'], userAccessLevel);
        return {
          id: level.id,
          label: level.name,
          url: levelIsLocked ? '/plans/upgrade' : `/path/${level.slug}`,
          isLocked: levelIsLocked,
        };
      });
    };

    const fetchPlaylist = async (): Promise<void> => {
      if (!pathID) return;
      try {
        const { playlists } = await Library.getPathByID(pathID);
        const levels = generatePathLevels(playlists);
        setPathLevels(levels);
      } catch (e) {
        console.error(e);
      }
    };

    if (playlist) {
      const levels = generatePathLevels(playlist);
      setPathLevels(levels);
    } else {
      fetchPlaylist();
    }
  }, [isFreemium, pathID, playlist, setPathLevels, userAccessLevel]);

  const handleClick = (id?: string): void => {
    if (id && id.indexOf('menu-path-level') > -1) {
      const level = +id[id.length - 1];
      trackEvent(EventNames.LEVEL_CLICKED, {
        fc_id: userID,
        is_locked: pathLevels[level - 1].isLocked,
        level,
      });
      segment.updateUser(userID, { currentLevel: getCurrentLevel(id) });
    }

    trackEvent(EventNames.NAVIGATION_CLICKED, {
      fc_id: userID,
      has_dropdown: id === MenuIDs.PATH,
      navigation_type: 'main',
      navigation_group: id === MenuIDs.PATH ? 'My Path' : undefined,
      navigation_label: id ? getNavigationLabel(id) : undefined,
    });

    // Don't close menu if opening/closing an accordion
    if (id !== MenuIDs.PATH) {
      handleCloseMenu();
    }

    if (id === MenuIDs.SIGN_OUT) {
      window.localStorage.clear();
    }
  };

  const handleDismissMenu = (): void => {
    trackEvent(EventNames.NAVIGATION_DISMISSED, {
      // eslint-disable-next-line @typescript-eslint/camelcase
      fc_id: userID,
    });
    handleCloseMenu();
  };

  return (
    <Drawer isOpen={isMenuVisible} onClose={handleDismissMenu} position="left" width={menuWidth}>
      <Menu
        dataId="primary"
        handleCloseMenu={handleDismissMenu}
        onClick={handleClick}
        pathname={location.pathname}
        isLoggedIn={hasLoadedCookie}
        profileUrl={!hasLAUSDCookie && PROFILE_URL ? PROFILE_URL : undefined}
        signOutUrl={getSignOutLink(hasLAUSDCookie)}
        width={menuWidth}
      >
        <>
          <MenuList>
            <MenuCTA
              label="Home"
              iconActive={getIcon(HomeFilled)}
              iconInactive={getIcon(Home)}
              id={MenuIDs.HOME}
              url={MenuPaths.HOME}
              LinkComponent={Link}
            />
            <MenuAccordion
              iconActive={getIcon(PathFilled)}
              iconInactive={getIcon(Path)}
              id={MenuIDs.PATH}
              label="My Path"
              url={MenuPaths.PATH}
            >
              {pathLevels?.map(
                (level, index): ReactNode => (
                  <MenuAccordionItem
                    key={level.id}
                    id={`menu-path-level-${index + 1}`}
                    label={level.label}
                    url={level.url}
                    isLocked={level.isLocked}
                    LinkComponent={Link}
                  />
                ),
              )}
            </MenuAccordion>
            {instrument && (
              <>
                <MenuCTA
                  iconActive={getIcon(SongsFilled)}
                  iconInactive={getIcon(Songs)}
                  id={MenuIDs.SONGS}
                  label="Songs"
                  url={`/${instrument}/songs`}
                  LinkComponent={Link}
                ></MenuCTA>
                <MenuCTA
                  iconActive={getIcon(PlayListsFill)}
                  iconInactive={getIcon(PlayLists)}
                  id={MenuIDs.COLLECTIONS}
                  label="Collections"
                  url={`/${instrument}/collections`}
                  LinkComponent={Link}
                ></MenuCTA>
                <MenuCTA
                  iconActive={getIcon(SkillsFilled)}
                  iconInactive={getIcon(Skills)}
                  id={MenuIDs.SKILLS}
                  label="Skills"
                  url={`/${instrument}/skills`}
                  LinkComponent={Link}
                ></MenuCTA>
              </>
            )}
            <MenuCTA
              label="Chord Challenge"
              iconActive={getIcon(ChordDiagram)}
              iconInactive={getIcon(ChordDiagram)}
              id={MenuIDs.CHORD_CHALLENGE}
              url={MenuPaths.CHORD_CHALLENGE}
              LinkComponent={Link}
            />
            <MenuCTA
              label="Favorites"
              iconActive={
                <>
                  <HeartFill fill={colors.black} aria-hidden />
                  <Spacer direction="vertical" />
                </>
              }
              iconInactive={
                <>
                  <Heart stroke="#595959" aria-hidden />
                  <Spacer direction="vertical" />
                </>
              }
              id={MenuIDs.FAVORITES}
              url={MenuPaths.FAVORITES}
              LinkComponent={Link}
            />
            <MenuCTA
              label="Articles"
              iconActive={getIcon(Articles)}
              iconInactive={getIcon(Articles)}
              id={MenuIDs.ARTICLES}
              url={MenuPaths.ARTICLES}
              LinkComponent={Link}
            />
            <MenuCTA
              label="What's New"
              iconActive={getIcon(MegaphoneFill)}
              iconInactive={getIcon(Megaphone)}
              id={MenuIDs.WHATS_NEW}
              url={MenuPaths.WHATS_NEW}
              LinkComponent={Link}
            />
          </MenuList>
          <MenuList>
            <MenuCTA
              iconInactive={getIcon(Support)}
              id={MenuIDs.SUPPORT}
              label="Support"
              target="_blank"
              url={MenuPaths.SUPPORT}
            />
            <MenuCTA
              iconInactive={getIcon(Toolkit)}
              id={MenuIDs.TOOLKIT}
              label="Tuner"
              target="_blank"
              url={MenuPaths.TOOLKIT}
            />
          </MenuList>
        </>
      </Menu>
    </Drawer>
  );
};

export default Navigation;
